/**
 * @module console
 * @description Implements the shared console-backed logger used by the
 * adapters package. The logger applies log-level filtering, prefixes, and
 * contextual child loggers without requiring an external logging backend.
 */

import { warnRuntimeAdapterMock } from "../runtime-warning";
import type { LoggerAdapter, LogLevel, LogMeta } from "./interface";

/**
 * Priority ordering of log levels.
 * Used to filter which logs are output based on minimum level.
 * Debug < Info < Warn < Error
 */
const LOG_LEVEL_PRIORITY: Record<LogLevel, number> = {
  debug: 0,
  info: 1,
  warn: 2,
  error: 3,
};

/**
 * Configuration options for console logger behavior.
 */
export interface ConsoleLoggerOptions {
  /** Minimum log level to display. Defaults to 'debug' in development, 'info' in production */
  minLevel?: LogLevel;
  /** Prefix prepended to every log message (e.g., '[MyApp]', '[adapter]') */
  prefix?: string;
}

/**
 * Creates a logger that outputs to browser/Node console.
 *
 * This is the default logger implementation for geenius adapters.
 * It filters logs by level and supports context via child loggers.
 *
 * Log levels are ordered by severity:
 * - debug: Detailed diagnostic information (disabled in production by default)
 * - info: General informational messages
 * - warn: Warning messages about unusual conditions
 * - error: Error messages about failures
 *
 * @param options - Configuration for logger behavior
 * @returns A LoggerAdapter instance that logs to console
 *
 * @example
 * ```ts
 * // Default setup - debug in dev, info in prod
 * const logger = createConsoleLoggerAdapter();
 *
 * // Custom minimum level
 * const quietLogger = createConsoleLoggerAdapter({ minLevel: 'warn' });
 * // Only warns and errors are printed
 *
 * // Custom prefix
 * const prefixedLogger = createConsoleLoggerAdapter({
 *   prefix: '[MyApp]',
 *   minLevel: 'info'
 * });
 * // Output: "[MyApp] Database connected"
 *
 * // Use in code
 * const dbLogger = await logger.child({ adapter: 'db', provider: 'convex' });
 * dbLogger.info('Connection established'); // "[geenius] Connection established { adapter, provider }"
 * ```
 */
export function createConsoleLoggerAdapter(
  options?: ConsoleLoggerOptions,
): LoggerAdapter {
  // Detect development environment
  const isDev =
    typeof process !== "undefined" && process.env?.NODE_ENV !== "production";
  // Use provided min level or pick based on environment
  const minLevel = options?.minLevel ?? (isDev ? "debug" : "info");
  // Use provided prefix or default
  const prefix = options?.prefix ?? "[geenius]";

  if (!isDev) {
    warnRuntimeAdapterMock(
      "[@geenius/adapters] console logger adapter is a MOCK for prototyping only. " +
        "Do NOT use in production. Use a real logger provider from a dedicated downstream package instead.",
    );
  }

  /**
   * Check if a given log level should be displayed.
   * Compares the level's priority against the minimum level.
   *
   * @internal
   */
  function shouldLog(level: LogLevel): boolean {
    return LOG_LEVEL_PRIORITY[level] >= LOG_LEVEL_PRIORITY[minLevel];
  }

  /**
   * Add prefix to a message.
   *
   * @internal
   */
  function formatMessage(message: string): string {
    return prefix ? `${prefix} ${message}` : message;
  }

  /**
   * Create a logger function with optional default metadata.
   * This function is called recursively to support child loggers.
   *
   * @internal
   */
  function createLogger(defaultMeta?: LogMeta): LoggerAdapter {
    /**
     * Internal log function that handles filtering, formatting, and output.
     *
     * @internal
     */
    async function log(
      level: LogLevel,
      message: string,
      meta?: LogMeta,
    ): Promise<void> {
      // Skip if below minimum log level
      if (!shouldLog(level)) return;

      // Merge default metadata with provided metadata
      const merged = defaultMeta ? { ...defaultMeta, ...meta } : meta;
      // Format message with prefix
      const formatted = formatMessage(message);

      // Output to console with optional metadata
      if (merged && Object.keys(merged).length > 0) {
        console[level](formatted, merged);
      } else {
        console[level](formatted);
      }
    }

    return {
      debug: (message, meta) => log("debug", message, meta),
      info: (message, meta) => log("info", message, meta),
      warn: (message, meta) => log("warn", message, meta),
      error: (message, meta) => log("error", message, meta),
      // Create child logger by merging metadata
      child: async (childMeta) =>
        createLogger({ ...defaultMeta, ...childMeta }),
    };
  }

  return createLogger();
}
